import {
  ApiError,
  formatMoney,
  type InventoryCost,
  type ProductListRow,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useDebouncedValue } from './image-library-grid.js'

interface StockLevelRow {
  productId: string
  locationId: string
  locationName: string
  available: number
}

/** "2 × Battery · Main store" — the sub-line under a fitted part's cost label (t1139). */
export function fittedPartSummary(cost: InventoryCost): string | null {
  if (!cost.componentProductId || cost.componentQty === null) return null
  const where = cost.componentLocationName ? ` · ${cost.componentLocationName}` : ''
  return `${cost.componentQty} × ${cost.componentProductName ?? 'part'}${where}`
}

/** Everything a fitted part touches: the cost list and the stock numbers. */
function useInvalidateCostsAndStock() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() }),
    ])
  }
}

/**
 * Take a counted part (a battery, say) out of inventory and fit it to one
 * device (t1139). The part's stock drops and its cost is added to the device's
 * cost basis — the device's sale price is never touched.
 */
export function FitPartForm({ unitId }: { unitId: string }) {
  const toast = useToast()
  const invalidate = useInvalidateCostsAndStock()
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  const [part, setPart] = useState<ProductListRow | null>(null)
  const [locationId, setLocationId] = useState('')
  const [qty, setQty] = useState('1')
  const [amount, setAmount] = useState('')
  const [amountTouched, setAmountTouched] = useState(false)

  const results = useQuery({
    queryKey: queryKeys.products.list({ q, type: 'stock', forFitting: true }),
    queryFn: () =>
      api.get<{ items: ProductListRow[] }>('/products', {
        query: { q, type: 'stock', isArchived: false, pageSize: 10 },
      }),
    enabled: q.length >= 2 && part === null,
  })

  const levels = useQuery({
    queryKey: queryKeys.stock.levels({ productId: part?.id }),
    queryFn: () => api.get<StockLevelRow[]>('/stock', { query: { productId: part?.id } }),
    enabled: part !== null,
  })
  const shelves = (levels.data ?? []).filter((l) => l.available > 0)

  const qtyNum = Number.parseInt(qty, 10)
  const qtyValid = Number.isInteger(qtyNum) && qtyNum > 0
  const defaultAmountCents = part?.costCents != null && qtyValid ? part.costCents * qtyNum : null
  const shownAmount = amountTouched
    ? amount
    : defaultAmountCents !== null
      ? (defaultAmountCents / 100).toFixed(2)
      : ''
  const selectedShelf = shelves.find((l) => l.locationId === locationId)

  function reset() {
    setPart(null)
    setSearch('')
    setLocationId('')
    setQty('1')
    setAmount('')
    setAmountTouched(false)
  }

  const fit = useMutation({
    mutationFn: () =>
      api.post('/costs', {
        unitId,
        kind: 'addon',
        label: part?.name,
        amountCents: shownAmount.trim() ? Math.round(Number(shownAmount) * 100) : null,
        componentProductId: part?.id,
        componentLocationId: locationId,
        componentQty: qtyNum,
      }),
    onSuccess: async () => {
      await invalidate()
      toast.success('Part fitted', 'Stock was reduced and the cost added to this device.')
      reset()
    },
    onError: (error: Error) =>
      toast.error('Could not fit the part', error instanceof ApiError ? error.message : undefined),
  })

  const canSubmit =
    part !== null &&
    locationId !== '' &&
    qtyValid &&
    (selectedShelf ? qtyNum <= selectedShelf.available : true) &&
    shownAmount.trim() !== '' &&
    Number.isFinite(Number(shownAmount))

  return (
    <div className="space-y-2">
      {part === null ? (
        <>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search inventory for a part (e.g. battery)"
            aria-label="Search inventory for a part"
          />
          {q.length >= 2 && !results.isFetching && (results.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-foreground-muted">No matching counted-stock products.</p>
          ) : null}
          {(results.data?.items.length ?? 0) > 0 ? (
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {results.data?.items.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                    onClick={() => setPart(product)}
                  >
                    <span>
                      <span className="block font-medium">{product.name}</span>
                      <span className="block font-identifier text-2xs text-foreground-muted">
                        {product.sku}
                      </span>
                    </span>
                    <span className="tabular text-foreground-muted">
                      {product.costCents != null ? formatMoney(product.costCents) : 'no cost set'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between rounded-md border border-border p-2.5 text-sm">
            <span className="font-medium">{part.name}</span>
            <Button variant="ghost" size="sm" onClick={reset}>
              Change
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger aria-label="Take from location">
                <SelectValue placeholder={levels.isLoading ? 'Loading…' : 'Take from…'} />
              </SelectTrigger>
              <SelectContent>
                {shelves.map((l) => (
                  <SelectItem key={l.locationId} value={l.locationId}>
                    {l.locationName} ({l.available} in stock)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputMode="numeric"
              placeholder="Qty"
              aria-label="Quantity used"
            />
            <Input
              value={shownAmount}
              onChange={(e) => {
                setAmountTouched(true)
                setAmount(e.target.value)
              }}
              inputMode="decimal"
              placeholder="Cost 0.00"
              aria-label="Total cost of the part"
            />
          </div>
          {shelves.length === 0 && !levels.isLoading ? (
            <p className="text-sm text-foreground-muted">This part is not in stock anywhere.</p>
          ) : null}
          {part.costCents == null ? (
            <p className="text-xs text-foreground-muted">
              This product has no cost set — enter what the part cost.
            </p>
          ) : (
            <p className="text-xs text-foreground-muted">
              Cost defaults to the product's current cost ({formatMoney(part.costCents)} each). The
              phone's sale price does not change.
            </p>
          )}
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              loading={fit.isPending}
              disabled={!canSubmit}
              onClick={() => fit.mutate()}
            >
              Fit part
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Removing an add-on. For a fitted part this asks whether it goes back on the
 * shelf (default) or is written off as used; a plain cost line just deletes.
 */
export function RemoveCostDialog({
  cost,
  onClose,
}: {
  cost: InventoryCost | null
  onClose: () => void
}) {
  const toast = useToast()
  const invalidate = useInvalidateCostsAndStock()
  const [restock, setRestock] = useState(true)
  const summary = cost ? fittedPartSummary(cost) : null

  const remove = useMutation({
    mutationFn: () => api.delete(`/costs/${cost?.id}${summary ? `?restock=${restock}` : ''}`),
    onSuccess: async () => {
      await invalidate()
      toast.success(summary && restock ? 'Removed — part put back in stock' : 'Cost removed')
      setRestock(true)
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={cost !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {cost?.label}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <p>This takes {cost ? formatMoney(cost.amountCents) : ''} back off the device's cost.</p>
          {summary ? (
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={restock}
                onChange={(e) => setRestock(e.target.checked)}
              />
              <span>
                Put {summary} back in stock
                <span className="block text-xs text-foreground-muted">
                  Leave unticked if the part was used up and shouldn't return to the shelf.
                </span>
              </span>
            </label>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
